import { ChooseOrgOptions } from '@ardoq/api-types';
import { Link, text1 } from '@ardoq/typography';
import { GhostButton } from '@ardoq/button';
import styled from 'styled-components';
import { colors, s16 } from '@ardoq/design-tokens';
import { Hero, Stack } from '@ardoq/layout';
import { authApi } from '@ardoq/api';
import { useEffect, useState } from 'react';
import { redirectToAuthErrorPage } from 'authentication/AuthErrorPage';
import { isArdoqError } from '@ardoq/common-helpers';
import { withBasePath } from 'basePath';
import { t } from '@ardoq/i18n';
import { getPreAppI18nLocale } from 'preAppI18nBootstrap';

const Text1 = styled.div`
  ${text1};
`;

const OrganizationList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${s16};

  button {
    border: 1px solid ${colors.grey300};
  }
`;

type ChooseOrgPageProps = ChooseOrgOptions;

const ChooseOrgPage = ({ organizations, email }: ChooseOrgPageProps) => {
  const i18nLocale = getPreAppI18nLocale();
  return (
    <Hero.Container>
      <Hero.Stack>
        <Hero.Header
          title={t(i18nLocale, 'auth:chooseOrg.title', 'Choose organization')}
        />
        <Hero.PlainTextContainer>
          <Text1>
            {t(
              i18nLocale,
              'auth:chooseOrg.intro',
              { email },
              'Your account {{email}} is currently linked to these organizations. Please select one to continue.'
            )}
          </Text1>
        </Hero.PlainTextContainer>
        <Hero.ActionsContainer>
          <Stack gap="medium" align="center">
            <OrganizationList>
              {organizations.map(({ name, label }, index) => (
                <GhostButton
                  key={index}
                  isFlexible
                  onClick={() => {
                    window.location.href = `/api/switchOrg?org=${label}`;
                  }}
                >
                  {name}
                </GhostButton>
              ))}
            </OrganizationList>
          </Stack>
        </Hero.ActionsContainer>
        <Hero.FooterContainer>
          <Stack gap="medium" align="center">
            <Link href="/logout" hideIcon type="secondary">
              {t(
                i18nLocale,
                'auth:chooseOrg.signInDifferentAccount',
                'Sign in with a different account'
              )}
            </Link>
          </Stack>
        </Hero.FooterContainer>
      </Hero.Stack>
    </Hero.Container>
  );
};

export const HandleChooseOrg = () => {
  const [chooseOrgOptions, setChooseOrgOptions] =
    useState<ChooseOrgOptions | null>(null);

  useEffect(() => {
    authApi
      .fetchChooseOrgOptions()
      .then(response => {
        if (isArdoqError(response)) {
          window.location.href = withBasePath('/');
        } else if (response.organizations?.length === 0) {
          window.location.href = withBasePath('/missing-org');
        } else {
          setChooseOrgOptions(response);
        }
      })
      .catch((error: Error) => {
        redirectToAuthErrorPage(
          error.message || 'Unhandled error when validating session.'
        );
      });
  }, []);

  if (chooseOrgOptions) {
    return (
      <ChooseOrgPage
        organizations={chooseOrgOptions.organizations}
        email={chooseOrgOptions.email}
      />
    );
  }
  return null;
};
